import { config } from "../config";

/**
 * Cloudflare Turnstile, the "I am human" check on EVERY way into ankhos.io
 * (owner 2026-10-24: "All login should have turnstile captcha"). radaccess
 * handler/turnstile.go checks the token on the FIRST step of each way in; the
 * steps after it carry a ticket that step issued.
 *
 * How the pieces meet:
 *   - <HumanCheck action="..."/> (components/HumanCheck.tsx) renders the
 *     widget and keeps the newest token here, per action;
 *   - every public request builder passes its body through withHuman(path,
 *     body), which adds captchaToken for a gated path and then resets the
 *     widget: a token is good for ONE call, failed calls included;
 *   - humanRefused(status, body) tells the widget a call was refused for the
 *     check, so it can say so.
 *
 * 🔑 OFF UNTIL KEYED. No config.turnstileSiteKey = no widget and no token, and
 * radaccess runs ungated until its own secret is set. The two are switched
 * on in that order: console first, then the server secret.
 */

export const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export type HumanAction = "signin" | "forgot" | "signup" | "invite";

/**
 * The gated routes, as paths under gatewayBase, and the action(s) its widget
 * may carry. The SAME list as radaccess app/accessrouters.go (pinned there by
 * TestEveryWayInRequiresTheHumanCheckOnceKeyed). Email start is the first step
 * of email sign-in AND of forgot password by email, so either screen's widget
 * serves it.
 */
export const HUMAN_GATED: Readonly<Record<string, HumanAction | readonly HumanAction[]>> = {
  "/auth/session": "signin",
  "/auth/otp/start": "signin",
  "/auth/email/start": ["signin", "forgot"],
  "/auth/widget": "signin",
  "/auth/idp/start": "signin",
  "/auth/password/forgot/widget": "forgot",
  "/auth/password/forgot/username": "forgot",
  "/auth/password/forgot/email": "forgot",
  "/auth/testcode": ["signin", "forgot"],
  "/auth/code": ["signin", "forgot"],
  "/operator/apply": "signup",
  "/operator/signup": "signup",
  "/sponsor/claim": "invite",
  "/operator/invite/accept": "invite",
  "/operator/invite/verify": "invite",
  "/operator/invite/testcode": "invite",
};

/**
 * The action for a path under gatewayBase, or a full URL under it; null = not
 * gated. Of a route's actions, the one whose widget holds a token, else the
 * one whose widget is on screen, else the first.
 */
export function humanActionFor(pathOrUrl: string): HumanAction | null {
  let path = pathOrUrl;
  const base = config().gatewayBase;
  if (path.startsWith(base)) path = path.slice(base.length);
  path = path.split("?")[0]!;
  const gated = HUMAN_GATED[path];
  if (!gated) return null;
  const actions: readonly HumanAction[] = typeof gated === "string" ? [gated] : gated;
  return actions.find((a) => slots.get(a)?.token) ?? actions.find((a) => slots.get(a)?.reset) ?? actions[0]!;
}

/** The public site key, or "" when the check is off. */
export function humanSiteKey(): string {
  try {
    return (config().turnstileSiteKey ?? "").trim();
  } catch {
    // Before /config.json loads (and in view tests) there is no check to show.
    return "";
  }
}

type Slot = { token: string; reset: (() => void) | null; refused: string };
const slots = new Map<HumanAction, Slot>();
const listeners = new Set<() => void>();

function slot(action: HumanAction): Slot {
  let s = slots.get(action);
  if (!s) {
    s = { token: "", reset: null, refused: "" };
    slots.set(action, s);
  }
  return s;
}

function changed() {
  for (const l of listeners) l();
}

/** For HumanCheck: keep the newest token ("" when it expired or failed). */
export function setHumanToken(action: HumanAction, token: string) {
  const s = slot(action);
  s.token = token;
  if (token) s.refused = "";
  changed();
}

/** For HumanCheck: how to get a fresh token after one is spent. */
export function setHumanReset(action: HumanAction, reset: (() => void) | null) {
  slot(action).reset = reset;
}

/** Whether a token is ready for action (always true when the check is off). */
export function humanReady(action: HumanAction): boolean {
  return !humanSiteKey() || slot(action).token !== "";
}

/** The message to show under the widget after a refusal, or "". */
export function humanRefusal(action: HumanAction): string {
  return slot(action).refused;
}

export function subscribeHuman(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The body to send to pathOrUrl: for a gated path with the check on, payload
 * plus captchaToken, and the token is spent (the widget is reset for the next
 * call). Anything else passes through unchanged.
 */
/**
 * Wait (up to timeoutMs) for a token for the call to pathOrUrl. The widget is
 * invisible unless Cloudflare needs a click ("interaction-only") and, after
 * each spent token, re-checks silently for a second or two: a click in that
 * window used to go out with no token and come back "complete the human
 * check" with nothing on screen to complete (owner 2026-10-25). Resolves at
 * once when the call is not gated or the check is off; on timeout the call
 * goes anyway and the server's refusal is shown.
 */
export function waitForHuman(pathOrUrl: string, timeoutMs = 10_000): Promise<void> {
  const action = humanActionFor(pathOrUrl);
  if (!action || !humanSiteKey() || slot(action).token) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      listeners.delete(check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => {
      if (slot(action).token) done();
    };
    const timer = setTimeout(done, timeoutMs);
    listeners.add(check);
  });
}

export function withHuman<T>(pathOrUrl: string, payload: T): T | (T & { captchaToken: string }) {
  const action = humanActionFor(pathOrUrl);
  if (!action || !humanSiteKey()) return payload;
  const s = slot(action);
  const token = s.token;
  s.token = "";
  if (s.reset) s.reset();
  changed();
  if (payload !== null && typeof payload === "object" && !Array.isArray(payload)) {
    return { ...(payload as object), captchaToken: token } as T & { captchaToken: string };
  }
  return payload;
}

/**
 * Note a radaccess refusal for the human check on pathOrUrl, so the widget
 * says what happened. Returns true when it was one.
 */
export function humanRefused(pathOrUrl: string, status: number, body: unknown): boolean {
  const action = humanActionFor(pathOrUrl);
  const error = body && typeof body === "object" ? (body as { error?: unknown }).error : undefined;
  if (!action || typeof error !== "string" || !error.startsWith("captcha_")) return false;
  slot(action).refused =
    status === 503
      ? "The human check could not be confirmed just now. Wait a moment and try again."
      : error === "captcha_required"
        ? "Complete the human check above, then try again."
        : "The human check expired. It has been refreshed: try again.";
  changed();
  return true;
}

export interface TurnstileOptions {
  sitekey: string;
  action: string;
  theme?: "auto" | "light" | "dark";
  size?: "normal" | "flexible" | "compact";
  appearance?: "always" | "execute" | "interaction-only";
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
}

export interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileOptions) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | null = null;

/** The Turnstile API, loading its script the first time. */
export function loadTurnstile(doc: Document = document): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!loading) {
    loading = new Promise<TurnstileApi>((resolve, reject) => {
      const s = doc.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not start")));
      s.onerror = () => {
        loading = null;
        reject(new Error("The human check could not load. Check your connection and try again."));
      };
      doc.head.appendChild(s);
    });
  }
  return loading;
}

/** Test seam: forget every token and listener. */
export function resetHumanForTests() {
  slots.clear();
  listeners.clear();
  loading = null;
}
